// ---------------------------------------------------------------------------
// Task #3875 — Contratto dati CRM master condiviso.
// I ~55 tipi risposta locali che vivevano qui sono stati sostituiti dai DTO
// Zod condivisi in @easyflow/shared (packages/shared/src/schemas/crm-master.ts),
// importati nei consumer con alias (es. `CrmMasterQuote as Quote`).
// Qui restano SOLO: la costante di base path, i tipi UI puri (props di
// componenti, stato locale, draft di richiesta) e i modelli client-side del
// jsonb installment_plan.
// ---------------------------------------------------------------------------
import React from 'react';
import type { CrmMasterQuoteItem } from '@easyflow/shared';

export const CRM_BASE = '/master/crm';

// ---------------------------------------------------------------------------
// Modello client-side delle righe del jsonb installment_plan (piani rate).
// Sul wire è jsonb: i numeri possono arrivare come stringhe.
// ---------------------------------------------------------------------------
export type InstallmentPlanRow = {
  installment_number?: number;
  number?: number;
  amount?: number | string;
  due_date?: string;
  notes?: string;
  description?: string;
};

// ---------------------------------------------------------------------------
// Draft di richiesta (stato locale di creazione preventivo): supertype
// permissivo delle righe item condivise + campi di editing solo-FE.
// ---------------------------------------------------------------------------
export type QuoteDraftItem = Partial<CrmMasterQuoteItem> & {
  name?: string;
  recurrence?: string;
};

export type NewQuoteDraft = {
  tenant_id?: string;
  pipeline_entry_id?: string;
  items: QuoteDraftItem[];
  notes?: string;
  payment_terms?: string;
};

// Stato locale del modal di setup pagamento (subset del preventivo scelto).
export type PaymentSetupState = {
  total_gross?: number | string;
  quote_number?: string;
  tenant_name?: string;
  contact_first_name?: string;
  subtotal_net?: number | string;
  total_vat?: number | string;
  id?: string;
};

// Target locale del flusso di rimodulazione rate (costruito dal FE a partire
// da una riga rata/contratto, non è una risposta API).
// Task #3875 — allineato al DTO condiviso CrmMasterInstallment (campi nullable sul wire).
export type RemodulateTarget = {
  invoice_id?: string | null;
  tenant_name?: string | null;
  contract_number?: string | null;
  contract_id?: string | null;
  invoice_number?: string | null;
  pipeline_entry_id?: string | null;
  id?: string;
};

// Task #3875 — draft locale form "nuova entry pipeline": `tenant_id` è un campo
// solo-form (non esiste sul wire della entry), quindi va dichiarato esplicitamente.
export type NewPipelineEntryDraft = Partial<import('@easyflow/shared').CrmMasterPipelineEntry> & { tenant_id?: string };

// Task #3875 — draft locale per l'editor voci preventivo (stato form, non wire):
// gli input testuali producono stringhe, i totali vengono ricalcolati con parseFloat.
export type QuoteItemDraft = {
  product_id: string;
  description: string;
  quantity: string | number;
  unit_price: string | number;
  discount_percent: string | number;
  vat_rate: string | number;
  vat_code: string;
};

// ---------------------------------------------------------------------------
// Tipi UI puri (props di componenti condivisi della pagina CRM).
// ---------------------------------------------------------------------------
export interface SelectOption { value: string; label: string }

export interface InputFieldProps {
  label?: string;
  value?: string | number | null;
  onChange?: (v: string) => void;
  type?: string;
  placeholder?: string;
  readOnly?: boolean;
  className?: string;
  error?: boolean | string;
  required?: boolean;
  uppercase?: boolean;
}

export interface SelectFieldProps {
  label?: string;
  value?: string | number | null;
  onChange?: (v: string) => void;
  options: SelectOption[];
  className?: string;
  error?: boolean;
  required?: boolean;
}

export interface ModalWrapperProps {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  children?: React.ReactNode;
  wide?: boolean;
}
